// ─────────────────────────────────────────────────────────────
// What the signed-in person may do in docs, and the ONE place the screen
// names docs' scopes. Their scopes come from `GET /api/v1/auth/me` (their
// zehn-id groups through the install's group grants, useSession().scopes);
// every check in the screen asks for a `SCOPE.*` here, never for a string, so
// renaming a scope (or folding several into one) is a change to this file
// only. The vocabulary is docs-api's (app/auth/scopes.py,
// `GET /api/v1/auth/scopes`). This is only about not offering what would come
// back 403; the API decides.
//
// Pure, so `npm test` checks it (scopes.test.js).
// ─────────────────────────────────────────────────────────────

// docs-api's vocabulary (app/auth/scopes.py, docs/ACCESS.md; the presets are
// the personas viewer, employee, records_officer, records_manager, admin).
const API = Object.freeze({
    // files and folders, whatever the relation (owner, shares) lets you reach
    FILES_READ: "files:read",
    FILES_WRITE: "files:write",
    FILES_UPDATE: "files:update",
    FILES_DELETE: "files:delete",
    // sharing with people, by guest link, with zehn-aip's library
    SHARES_WRITE: "shares:write",
    // signing and agreement
    SIGNING_WRITE: "signing:write",
    SIGNING_SIGN: "signing:sign",
    // the template library: every template, not only your own
    TEMPLATES_MANAGE: "templates:manage",
    // records management: the file plan, its cases, the journals
    RECORDS_READ: "records:read",
    RECORDS_FILE: "records:file",
    RECORDS_MANAGE: "records:manage",
    RECORDS_REVIEW: "records:review",
    RECORDS_DISPOSE: "records:dispose",
    // Whoever administers docs: its console (settings and group access,
    // policies, the stamp, webhooks, indexing, audit, storage, retention, API
    // keys); also unlocks or hands over anyone's file.
    ADMIN: "docs:admin",
});

/**
 * What the screen asks for, by name. Each is docs-api's scope for that
 * action: most are one of the above as is; the rest are the scope docs-api
 * checks for something the screen names on its own.
 */
export const SCOPE = Object.freeze({
    ...API,
    // hand anyone's file or folder over to somebody else (taking back what
    // someone who left made), and lift anyone's lock on a file
    HAND_OVER_ANY: API.ADMIN,
    UNLOCK_ANY: API.ADMIN,
    // the console
    CONSOLE: API.ADMIN,
});

/** docs-api's scopes, each once. */
export const API_SCOPES = Object.freeze(Object.values(API));

/**
 * Whether `scopes` (the person's, from the API) grant every one of `wanted`
 * (one scope or a list). The install's break-glass admin (`kind` "admin")
 * holds every scope, as docs-api says. An empty list is a real answer: no
 * scope, every check fails.
 */
export function holdsScope(scopes, wanted, { admin = false } = {}) {
    if (admin) return true;
    if (!Array.isArray(scopes)) return false;
    const list = Array.isArray(wanted) ? wanted : [wanted];
    return list.every((scope) => typeof scope === "string" && scopes.includes(scope));
}

/** Whether `scopes` grant at least one of `wanted` (a section several scopes open). */
export function holdsAnyScope(scopes, wanted, { admin = false } = {}) {
    if (admin) return true;
    return (Array.isArray(wanted) ? wanted : [wanted]).some((scope) => holdsScope(scopes, scope));
}

/** Whether `user` is the install's break-glass admin (docs-api's `get_admin_user`). */
export const isBreakGlassAdmin = (user) => user?.kind === "admin";
